import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './auth';
import { listMyUploads } from './uploads';
import { refreshUpload, deleteUpload, updateUpload, ApiError } from './api';
import { config } from './config';
import { StatusBadge, formatRelativeTime } from './ui';
import { ItemCard } from './item-card';
import { AddNewTile } from './add-new-tile';
import { ShareIconAction, ConfirmIconAction } from './quick-actions';
import {
  ListToolbar,
  SortOrder,
  ViewMode,
  sortItems,
  filterByTitle,
  useViewMode,
  useSelection,
  SelectionBar,
} from './list-controls';
import { mapWithConcurrency } from './concurrency';
import { Upload } from './types';

/** Human-readable reason for a single bulk-delete failure, matching the codes `deleteUpload`
 * already throws for the single-item action (see `deleteError` in `LibraryItem` below). */
function describeDeleteFailure(reason: unknown): string {
  if (reason instanceof ApiError) {
    if (reason.message === 'still_shared') return 'still shared';
    if (reason.message === 'early_deletion_fee_risk') return 'not yet eligible for deletion';
  }
  return 'an error';
}

function summarizeFailures(reasons: unknown[]): string {
  const counts = new Map<string, number>();
  for (const reason of reasons) {
    const desc = describeDeleteFailure(reason);
    counts.set(desc, (counts.get(desc) ?? 0) + 1);
  }
  return [...counts.entries()].map(([desc, n]) => `${n} ${desc}`).join(', ');
}

const TERMINAL: ReadonlySet<Upload['status']> = new Set(['ready', 'failed']);

export function LibraryPage() {
  const { identityId } = useAuth();
  const queryClient = useQueryClient();
  const [sort, setSort] = useState<SortOrder>('default');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useViewMode('library');
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkSummary, setBulkSummary] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);
  const selection = useSelection<Upload>((u) => u.uploadId);

  const query = useQuery({
    queryKey: ['library', identityId],
    enabled: !!identityId,
    queryFn: () => listMyUploads(identityId!),
  });

  const refreshMutation = useMutation({
    mutationFn: (uploadId: string) => refreshUpload(uploadId),
    onSuccess: (updated) => {
      queryClient.setQueryData<Upload[] | undefined>(['library', identityId], (prev) =>
        prev?.map((row) => (row.uploadId === updated.uploadId ? updated : row)),
      );
    },
  });

  const allRows = query.data ?? [];
  // Recomputed plainly each render (no useMemo) — allRows is a fresh array reference whenever
  // query.data is undefined, so memoizing on it wouldn't actually skip work; sorting/filtering a
  // few hundred items is cheap enough at this project's catalog scale (see design.md) that it
  // doesn't need memoizing regardless.
  const visibleRows = sortItems(filterByTitle(allRows, search, (r) => r.filename), sort, (r) => r.filename);

  async function handleBulkDelete() {
    const ids = [...selection.selectedIds];
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} video${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBulkPending(true);
    setBulkSummary(null);
    const results = await mapWithConcurrency(ids, (id) => deleteUpload(id), 8);
    const succeeded = results.filter((r) => r.status === 'fulfilled').map((r) => r.item);
    const failed = results.filter((r) => r.status === 'rejected');
    if (succeeded.length > 0) {
      const succeededSet = new Set(succeeded);
      queryClient.setQueryData<Upload[] | undefined>(['library', identityId], (prev) =>
        prev?.filter((r) => !succeededSet.has(r.uploadId)),
      );
      succeeded.forEach((id) => selection.toggle(id));
    }
    setBulkSummary(
      failed.length === 0
        ? `Deleted ${succeeded.length} video${succeeded.length === 1 ? '' : 's'}.`
        : `Deleted ${succeeded.length} of ${ids.length}. ${failed.length} couldn't be deleted — ${summarizeFailures(failed.map((f) => f.reason))}.`,
    );
    setBulkPending(false);
  }

  // Background refresh for non-terminal rows with exponential backoff.
  useEffect(() => {
    const nonTerminal = allRows.filter((r) => !TERMINAL.has(r.status));
    if (nonTerminal.length === 0) return;
    let delay = 5_000;
    const maxDelay = 60_000;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      for (const row of nonTerminal) {
        if (cancelled) return;
        try {
          await refreshMutation.mutateAsync(row.uploadId);
        } catch {
          // Swallow — the user can see stale state and refresh manually.
        }
      }
      delay = Math.min(delay * 2, maxDelay);
      if (!cancelled) setTimeout(tick, delay);
    };
    const timer = setTimeout(tick, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // refreshMutation is stable across renders thanks to react-query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows.map((r) => `${r.uploadId}:${r.status}`).join('|')]);

  if (!identityId || query.isLoading) {
    return <p className="muted">Loading your library…</p>;
  }

  if (allRows.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <h2>You haven't uploaded any videos yet</h2>
        <p className="muted">Upload a video to get started.</p>
        <Link to="/upload">
          <button>Upload your first video</button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <h1>My library</h1>
      <ListToolbar
        sort={sort}
        onSortChange={setSort}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onSearch={setSearch}
        searchPlaceholder="Search your library…"
        selectionMode={selectionMode}
        onToggleSelectionMode={() => {
          setSelectionMode((v) => !v);
          selection.clear();
          setBulkSummary(null);
        }}
      />
      {selectionMode && (
        <SelectionBar count={selection.count} onSelectAll={() => selection.selectAll(visibleRows)} onClear={selection.clear}>
          <button onClick={handleBulkDelete} disabled={selection.count === 0 || bulkPending}>
            Delete selected
          </button>
        </SelectionBar>
      )}
      {bulkSummary && <p className="muted">{bulkSummary}</p>}
      {viewMode === 'grid' ? (
        <div className="item-grid">
          <AddNewTile viewMode={viewMode} label="Upload a video" to="/upload" />
          {visibleRows.map((row) => (
            <LibraryItem
              key={row.uploadId}
              row={row}
              viewMode={viewMode}
              selectionMode={selectionMode}
              selected={selection.isSelected(row.uploadId)}
              onToggleSelected={() => selection.toggle(row.uploadId)}
            />
          ))}
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          <AddNewTile viewMode={viewMode} label="Upload a video" to="/upload" />
          {visibleRows.map((row) => (
            <LibraryItem
              key={row.uploadId}
              row={row}
              viewMode={viewMode}
              selectionMode={selectionMode}
              selected={selection.isSelected(row.uploadId)}
              onToggleSelected={() => selection.toggle(row.uploadId)}
            />
          ))}
        </ul>
      )}
      {visibleRows.length === 0 && <p className="muted">No uploads match your search.</p>}
    </div>
  );
}

function LibraryItem({
  row,
  viewMode,
  selectionMode,
  selected,
  onToggleSelected,
}: {
  row: Upload;
  viewMode: ViewMode;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelected: () => void;
}) {
  const { identityId } = useAuth();
  const queryClient = useQueryClient();

  const patchRow = (patch: Partial<Upload>) => {
    queryClient.setQueryData<Upload[] | undefined>(['library', identityId], (prev) =>
      prev?.map((r) => (r.uploadId === row.uploadId ? { ...r, ...patch } : r)),
    );
  };

  const refreshMutation = useMutation({
    mutationFn: () => refreshUpload(row.uploadId),
    onSuccess: (updated) => patchRow(updated),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteUpload(row.uploadId),
    onSuccess: () => {
      queryClient.setQueryData<Upload[] | undefined>(['library', identityId], (prev) =>
        prev?.filter((r) => r.uploadId !== row.uploadId),
      );
    },
  });

  const deleteError =
    deleteMutation.error instanceof ApiError && deleteMutation.error.message === 'early_deletion_fee_risk'
      ? "This video's source file has been archived and can't be deleted yet."
      : deleteMutation.error instanceof ApiError && deleteMutation.error.message === 'still_shared'
        ? 'Unshare this video before deleting it.'
        : deleteMutation.error
          ? "Couldn't delete — try again."
          : null;

  const shareUrl = `https://${config.spaDomain}/watch/${row.uploadId}`;

  return (
    <ItemCard
      href={`/library/${row.uploadId}`}
      thumbnailUrl={row.thumbnailUrl}
      title={row.filename}
      viewMode={viewMode}
      selectionMode={selectionMode}
      checkbox={{ checked: selected, onChange: onToggleSelected }}
      onRename={async (next) => {
        await updateUpload(row.uploadId, { filename: next });
        patchRow({ filename: next });
      }}
      meta={
        <>
          <div>
            <StatusBadge status={row.status} />
            <span className="muted" style={{ marginLeft: 8 }}>
              Updated {formatRelativeTime(row.updatedAt)}
            </span>
          </div>
          {row.status === 'failed' && row.errorMessage && (
            <p className="error" style={{ marginTop: 4 }}>
              {row.errorMessage}
            </p>
          )}
          {deleteError && (
            <p className="error" style={{ marginTop: 4 }}>
              {deleteError}
            </p>
          )}
        </>
      }
      actions={
        <>
          <button
            className="icon-button"
            aria-label="Refresh status"
            title="Refresh status"
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
          >
            🔄
          </button>
          <ShareIconAction shareUrl={shareUrl} />
          <ConfirmIconAction
            icon="🗑️"
            label="Delete"
            confirmMessage="Delete this video? This cannot be undone."
            onConfirm={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          />
        </>
      }
    />
  );
}
